/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  IAny,
  IDetailComponentProps,
  IEmaasEnergyUsageComponentDataV2,
  ISiteCard
} from '../../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card } from '@scuf/common';
import EnergyConsumptionByEquipmentTypeDetail from './EnergyConsumptionByEquipmentTypeDetail';
type IEnergyData = IEmaasEnergyUsageComponentDataV2;
interface IEnergySavingByMeter extends ISiteCard {
  data: IEnergyData;
  UtilityBillType: string;
  subscriptionType: string;
  requestContext?: IAny;
}

const energyScopeSavingsByMeterComponentContent = (
  props: IEnergySavingByMeter
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, UtilityBillType, requestContext } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  const childProps = {
    data,
    UtilityBillType,
    requestContext
  };
  return (
    <div>
      <EnergyConsumptionByEquipmentTypeDetail {...childProps} />
    </div>
  );
};

const EnergyScopeSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(energyScopeSavingsByMeterComponentContent)
);

interface IEnergyConsumptionByEquipmentTypeComponentProps
  extends IDetailComponentProps {
  isMeterListLoading: boolean;
  onRefresh: () => Promise<void>;
  meterListStatusCode: number | null;
}

const energyConsumptionByEquipmentTypeComponent = (
  props: IEnergyConsumptionByEquipmentTypeComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    dataConnector,
    UtilityBillType,
    subscriptionType,
    requestContext,
    isMeterListLoading,
    onRefresh,
    meterListStatusCode
  } = props;

  // const onRefresh = () =>
  //   dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const { all, loading, error, status } = dataConnector.data;
  const carbonScopeOneSavingsData = (all as unknown) as IEnergyData;
  const isDataAvailable = carbonScopeOneSavingsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [subscriptionCheck, setSubscriptionCheck] = useState('Basic');
  const [data, setData] = useState({} as IEnergyData);

  useEffect(() => {
    if (meterListStatusCode && meterListStatusCode === 200) {
      setIsLoading(loading);
      if ((isDataAvailable || error || status) && !loading) {
        if (isDataAvailable) {
          setData(carbonScopeOneSavingsData);
        }
        setErrorInfo(error);
        setStatusCode(status);
        setSubscriptionCheck(data?.subscriptionType);
      }
    } else {
      setIsLoading(false);
      setStatusCode(meterListStatusCode);
    }
  }, [isDataAvailable, loading, status, meterListStatusCode]);

  return (
    <Card className="Savemeter energyDisaggregation">
      <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        {t(d.ENERGYCONSUMPTIONBYEQUIPMENTTYPE)}
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <EnergyScopeSavingsByMeterComponentWithLoader
            loading={isLoading || isMeterListLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={
              meterListStatusCode === 204 ? t(d.NODATA) : t(d.NODATAAVAILABLE)
            }
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType!}
            subscriptionType={subscriptionType!}
            requestContext={requestContext}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default energyConsumptionByEquipmentTypeComponent;
